const mockStorage = new Map();
function createMockRouterProxy(path) {
    return new Proxy({}, {
        get(_target, prop) {
            const currentPath = [...path, prop];
            const pathKey = currentPath.join('.');
            // Check if this is one of our mock methods
            if (prop === 'success') {
                return (data) => {
                    const procedurePath = currentPath.slice(0, -1).join('.');
                    mockStorage.set(procedurePath, { type: 'success', data });
                };
            }
            if (prop === 'fail') {
                return (error) => {
                    const procedurePath = currentPath.slice(0, -1).join('.');
                    mockStorage.set(procedurePath, { type: 'fail', error });
                };
            }
            if (prop === 'loading') {
                return () => {
                    const procedurePath = currentPath.slice(0, -1).join('.');
                    mockStorage.set(procedurePath, { type: 'loading' });
                };
            }
            // Continue building the path
            return createMockRouterProxy(currentPath);
        }
    });
}
/**
 * Creates a mocked version of the router client that respects mock configurations
 *
 * @example
 * ```typescript
 * import { routerClient } from './client-rpc-router';
 * import { mockRouterClient, createMockedRouterClient } from '@nest/common-core-frontend-react';
 *
 * // Set up mocks
 * mockRouterClient(routerClient).identity.getIdentityOverviewQuery.success({ data: 1 });
 *
 * // Create mocked client that will use the mocks
 * const mockedClient = createMockedRouterClient(routerClient);
 *
 * // This will return the mocked data instead of making HTTP request
 * const result = await mockedClient.identity.getIdentityOverviewQuery.execute();
 * ```
 *
 * @param originalClient - The original router client
 * @returns A mocked router client that checks for mocks before making real requests
 */
export function createMockedRouterClient(originalClient) {
    function createMockedRouterProxy(path) {
        return new Proxy({}, {
            get(_target, prop) {
                if (prop === 'execute') {
                    return (args) => {
                        const pathKey = path.join('.');
                        const mock = mockStorage.get(pathKey);
                        if (mock != null) {
                            switch (mock.type) {
                                case 'success':
                                    return Promise.resolve(mock.data);
                                case 'fail':
                                    return Promise.reject(mock.error);
                                case 'loading':
                                    return new Promise(() => { }); // Never resolves
                            }
                        }
                        // Fallback to original client
                        let originalTarget = originalClient;
                        for (const segment of path) {
                            originalTarget = originalTarget[segment];
                        }
                        return originalTarget.execute(args);
                    };
                }
                return createMockedRouterProxy([...path, prop]);
            }
        });
    }
    return createMockedRouterProxy([]);
}
/**
 * Creates a mock interface for setting up mocks for router procedures
 *
 * @example
 * ```typescript
 * import { routerClient } from './client-rpc-router';
 * import { mockRouterClient } from '@nest/common-core-frontend-react';
 *
 * // Set up successful response
 * mockRouterClient(routerClient).identity.getIdentityOverviewQuery.success({
 *   id: 1,
 *   name: 'Test User'
 * });
 *
 * // Set up error response
 * mockRouterClient(routerClient).auth.login.fail({
 *   message: 'Invalid credentials'
 * });
 *
 * // Set up loading state (never resolves)
 * mockRouterClient(routerClient).data.getUsers.loading();
 * ```
 *
 * @param routerClient - The router client (used only for type inference)
 * @returns A mock interface with success/fail/loading methods for each procedure
 */
export function mockRouterClient(routerClient) {
    return createMockRouterProxy([]);
}
/**
 * Check if a specific procedure path has a mock configured
 * @param path - Array of path segments (e.g., ['identity', 'getIdentityOverviewQuery'])
 * @returns True if mock exists for this path
 */
export function hasMock(path) {
    const pathKey = path.join('.');
    return mockStorage.has(pathKey);
}
/**
 * Get the mock response for a specific procedure path
 * @param path - Array of path segments
 * @returns Promise that resolves/rejects/never based on mock configuration
 */
export function getMockResponse(path) {
    const pathKey = path.join('.');
    const mock = mockStorage.get(pathKey);
    if (mock == null) {
        throw new Error(`No mock configured for path: ${pathKey}`);
    }
    switch (mock.type) {
        case 'success':
            return Promise.resolve(mock.data);
        case 'fail':
            return Promise.reject(mock.error);
        case 'loading':
            return new Promise(() => { }); // Never resolves
        default:
            throw new Error(`Unknown mock type: ${mock.type}`);
    }
}
/**
 * Clear all configured mocks
 */
export function clearMocks() {
    mockStorage.clear();
}
/**
 * Clear mock for a specific procedure
 * @param path - Array of path segments to clear
 */
export function clearMock(path) {
    const pathKey = path.join('.');
    mockStorage.delete(pathKey);
}
/**
 * Get all currently configured mocks
 * @returns Object with path keys and their mock configurations
 */
export function listMocks() {
    const result = {};
    for (const [key, value] of mockStorage.entries()) {
        result[key] = value;
    }
    return result;
}
//# sourceMappingURL=mock-router-client.js.map